import { SeriesType } from "@tether-web-portal/components/property-graph/GraphUtils/SeriesType";
import { SeriesTypeReading } from "@tether-web-portal/components/property-graph/GraphUtils/SeriesTypeReading";
import {
  RoomLocationAggregate,
  RoomLocationAggregatedMetric,
} from "@tether-web-portal/types/RoomLocation";

const REGION_OCCUPANCY_KEY = SeriesTypeReading[SeriesType.RegionOccupancy];
const OCCUPANCY_PEOPLE_KEY = SeriesTypeReading[SeriesType.OccupancyPeopleCount];
const OCCUPANCY_REGION_COUNT_KEY = SeriesTypeReading[SeriesType.OccupancyRegionPeopleCount];
const OCCUPIED_KEY = SeriesTypeReading[SeriesType.Occupied];
const OCCUPIED_PERCENTAGE_KEY = SeriesTypeReading[SeriesType.OccupiedPercentage];
const TOTAL_IN_KEY = SeriesTypeReading[SeriesType.OccupancyTotalIn];
const TOTAL_OUT_KEY = SeriesTypeReading[SeriesType.OccupancyTotalOut];

export type MetricReducer = "max" | "min" | "mean" | "last";

/**
 * Which occupancy metric a given room is reporting. Decided ONCE per room
 * (see `detectRoomOccupancyMetric`) and then held fixed across every bucket
 * for that room — mixing two within one room's aggregates produces
 * semantically nonsensical totals (regions-occupied plus people-present,
 * or presence flags plus people-counts).
 *
 *   - "region"   — multi-zone sensor that reports `regionOccupancy` (count
 *                  of zones with an occupant). The room's "Max Occupancy"
 *                  reads as the zone count.
 *   - "people"   — counting sensor that reports `occupancy` (an integer
 *                  headcount). The default.
 *   - "occupied" — presence-only sensor that reports `occupied` /
 *                  `occupiedPercentage` (a 0..1 fraction-of-time-occupied
 *                  flag) but NEITHER `regionOccupancy` NOR `occupancy`.
 *                  The room's "Max Occupancy" collapses to a binary
 *                  0 / 1 indicator ("was the space used in this bucket?")
 *                  — that's the best signal a presence sensor can give.
 */
export type OccupancyMetricChoice = "region" | "people" | "occupied";

/**
 * Decide which occupancy metric a room is reporting. Priority order:
 *
 *   1. ANY bucket has `regionOccupancy` → "region" (and we then refuse to
 *      silently fall back to `occupancy` for stray buckets — that was the
 *      bug rule 4 fixes).
 *   2. ANY bucket has `occupancy` → "people" — the headcount path.
 *   3. ANY bucket has `occupied` or `occupiedPercentage` → "occupied" — a
 *      presence sensor that only tells us "in use at all?". Without this
 *      branch the room ended up with `occupancyMax = 0` for every bucket
 *      and the Occupancy columns / tiles read blank, even though Usage %
 *      (which reads `occupied.mean` directly via `getBucketOccupiedFraction`)
 *      lit up correctly.
 *   4. Otherwise return "people" — preserves the historical default for
 *      rooms whose API response had nothing at all (callers see
 *      `getRoomBucketOccupancyCount` return undefined and treat the
 *      bucket as 0).
 *
 * This is the per-room lock-in referenced by rule 4 in the canonical
 * occupancy semantics.
 */
export const detectRoomOccupancyMetric = (
  buckets: RoomLocationAggregate[],
): OccupancyMetricChoice => {
  let hasPeople = false;
  let hasOccupied = false;
  for (const b of buckets) {
    if (b.metrics[REGION_OCCUPANCY_KEY]) return "region";
    if (b.metrics[OCCUPANCY_PEOPLE_KEY]) hasPeople = true;
    if (b.metrics[OCCUPIED_KEY] || b.metrics[OCCUPIED_PERCENTAGE_KEY]) hasOccupied = true;
  }
  if (hasPeople) return "people";
  if (hasOccupied) return "occupied";
  return "people";
};

/**
 * Read the room-level occupancy count from a bucket through the room's
 * committed metric choice.
 *
 *   - "region":   read `regionOccupancy`; undefined when this bucket
 *                 lacks it (so totals see 0, never a silent metric swap).
 *   - "people":   read `occupancy` (the `occupancyPeopleCount` series).
 *   - "occupied": collapse `occupied` / `occupiedPercentage` to a binary
 *                 0 / 1 — the only meaningful integer count for a
 *                 presence sensor. The reducer-to-value mapping is:
 *                   max  → 1 if the bucket was ever occupied
 *                   min  → 1 if it was occupied throughout
 *                   mean → `occupied.mean` (0..1, time-weighted) — caller
 *                          ceils it for row display
 *                   last → 1 if the latest sample was occupied
 *                 `occupiedPercentage` is used as the fallback when
 *                 `occupied` isn't emitted; both express the same
 *                 fraction-of-time-occupied semantic.
 *
 * Never take `min(regionOccupancy, occupancy)` — that was the old
 * `pickLowestOccupancyMetric` which treated the two as interchangeable
 * people-counts when they measure different things.
 */
export const getRoomBucketOccupancyCount = (
  bucket: RoomLocationAggregate,
  reducer: MetricReducer,
  metricChoice: OccupancyMetricChoice,
): number | undefined => {
  if (metricChoice === "region") {
    const regionMetric = bucket.metrics[REGION_OCCUPANCY_KEY];
    return regionMetric ? readReducer(regionMetric, reducer) : undefined;
  }
  if (metricChoice === "occupied") {
    const occupiedMetric =
      bucket.metrics[OCCUPIED_KEY] ?? bucket.metrics[OCCUPIED_PERCENTAGE_KEY];
    if (!occupiedMetric) return undefined;
    const raw = readReducer(occupiedMetric, reducer);
    if (raw === undefined) return undefined;
    if (reducer === "mean") return raw; // preserve the fractional value
    return raw > 0 ? 1 : 0;
  }
  const peopleMetric = bucket.metrics[OCCUPANCY_PEOPLE_KEY];
  return peopleMetric ? readReducer(peopleMetric, reducer) : undefined;
};

/**
 * Variant for room-LOCATION (region/line) endpoints. Prefers the per-region
 * `count` metric (which is the actual people count for that region) before
 * falling back to the parent room's committed metric. The caller must pass
 * the parent room's `metricChoice` so sibling region buckets stay consistent
 * with the room-level aggregates.
 */
export const getRoomLocationBucketOccupancyCount = (
  bucket: RoomLocationAggregate,
  reducer: MetricReducer,
  metricChoice: OccupancyMetricChoice,
): number | undefined => {
  const countMetric = bucket.metrics[OCCUPANCY_REGION_COUNT_KEY];
  if (countMetric) return readReducer(countMetric, reducer);
  return getRoomBucketOccupancyCount(bucket, reducer, metricChoice);
};

const readReducer = (
  metric: RoomLocationAggregatedMetric,
  reducer: MetricReducer,
): number | undefined => {
  const value = metric[reducer];
  if (value !== undefined) return value;
  // Fall back to max — best available summary when the requested reducer
  // isn't present. (Notably, `.last` is dropped from room-level aggregates
  // by the API — see the audit, flag 4.)
  return metric.max;
};

/**
 * The API injects a synthetic `{occupancy: 0}` sample at the property's
 * daily reset time (see audit flag H10). We can't positively distinguish
 * this from a genuinely empty bucket without server-side support, so for
 * now we treat all zero-value buckets equally — resets are normally
 * outside monitoring hours anyway, so the monitoring-hours filter masks
 * them for the downstream stats that matter (min / mean / usage %).
 *
 * This shim exists so callers don't have to special-case the feature gap;
 * when the API gains a reset flag we can wire it here without touching
 * the model.
 */
export const looksLikeOccupancyResetBucket = (_bucket: RoomLocationAggregate): boolean => false;

export const getBucketOccupiedFraction = (bucket: RoomLocationAggregate): number => {
  const occupied = bucket.metrics[OCCUPIED_KEY];
  if (occupied) return occupied.mean;
  const percentage = bucket.metrics[OCCUPIED_PERCENTAGE_KEY];
  return percentage?.mean ?? 0;
};

export const getBucketTotalIn = (bucket: RoomLocationAggregate): number =>
  bucket.metrics[TOTAL_IN_KEY]?.delta ?? 0;

export const getBucketTotalOut = (bucket: RoomLocationAggregate): number =>
  bucket.metrics[TOTAL_OUT_KEY]?.delta ?? 0;
